import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Dialog } from '@/shared/ui/Dialog';

/** Une touche tapée dans un champ ne doit jamais déclencher un raccourci. */
function typing(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

const ROWS = [
  { keys: ['/'], label: 'web:shortcuts.search' },
  { keys: ['↑', 'K'], label: 'web:shortcuts.previous' },
  { keys: ['↓', 'J'], label: 'web:shortcuts.next' },
  { keys: ['L'], label: 'web:shortcuts.love' },
  { keys: ['M'], label: 'web:shortcuts.mute' },
  { keys: ['Échap'], label: 'web:shortcuts.close' },
  { keys: ['?'], label: 'web:shortcuts.open' },
] as const;

/**
 * Les raccourcis clavier de l'ordinateur : « ? » ouvre leur aide, « / » mène à
 * la recherche. Ceux du fil plein écran (↑ ↓ J K L M) sont posés par le fil
 * lui-même ; ils sont rappelés ici pour qu'on les trouve.
 */
export function ShortcutsDialog() {
  const { t } = useTranslation(['web', 'common']);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey || typing(event.target)) {
        return;
      }
      if (event.key === '?') {
        event.preventDefault();
        setOpen(true);
      } else if (event.key === '/') {
        event.preventDefault();
        void navigate('/explore');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  return (
    <Dialog open={open} onOpenChange={setOpen} title={t('web:shortcuts.title')}>
      <dl className="flex flex-col">
        {ROWS.map((row) => (
          <div key={row.label} className="flex items-center justify-between gap-4 border-b border-line-soft py-2.5 last:border-b-0">
            <dt className="text-subtle text-fg">{t(row.label)}</dt>
            <dd className="flex gap-1.5">
              {row.keys.map((key) => (
                <kbd
                  key={key}
                  className="min-w-7 rounded-xs border border-line bg-sunken px-1.5 py-0.5 text-center font-mono text-caption text-fg-strong"
                >
                  {key}
                </kbd>
              ))}
            </dd>
          </div>
        ))}
      </dl>
    </Dialog>
  );
}
